/**
 * Login — Eldritch Eye edition.
 * Email-or-phone identifier, Telegram widget, "Recordarme" + autofill hints,
 * code-rain background, blood-red accents.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useWebAuth } from "@/lib/webAuth";
import { getDeviceFingerprint } from "@/lib/deviceFingerprint";
import webApi from "@/lib/webApi";
import CodeRain from "@/components/effects/CodeRain";
import { Loader2, AtSign, Lock, Eye } from "lucide-react";

function formatError(err) {
  if (err && typeof err === "object" && (err.response || err.request || err.message)) {
    const detail = err.response?.data?.detail;
    if (detail) {
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
      return String(detail);
    }
    if (err.response?.status) {
      return `Error del servidor (HTTP ${err.response.status}). Intenta de nuevo.`;
    }
    if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      return "Sin conexión con el servidor. Revisa tu internet.";
    }
    if (err.code === "ECONNABORTED") return "Tiempo agotado. Intenta de nuevo.";
    return err.message || "Algo salió mal. Intenta de nuevo.";
  }
  const detail = err;
  if (!detail) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

const REMEMBER_KEY = "iarisk_remember_me";

export default function WebLogin() {
  const nav = useNavigate();
  const { loginIdentifier, user, loading } = useWebAuth();
  const [identifier, setIdentifier] = useState(() => {
    try {
      return localStorage.getItem("iarisk_last_identifier") || "";
    } catch {
      return "";
    }
  });
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => {
    try {
      return localStorage.getItem(REMEMBER_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) nav("/chat", { replace: true });
  }, [loading, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const fp = await getDeviceFingerprint();
      const id = identifier.trim();
      const idForApi = id.includes("@") ? id.toLowerCase() : id;
      await loginIdentifier(idForApi, password, fp, remember);
      try {
        localStorage.setItem(REMEMBER_KEY, remember ? "1" : "0");
        if (remember) localStorage.setItem("iarisk_last_identifier", idForApi);
        else localStorage.removeItem("iarisk_last_identifier");
      } catch (storageErr) {
        console.debug("[login] remember write failed:", storageErr?.message);
      }
      nav("/chat", { replace: true });
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-[100dvh] flex items-center justify-center bg-obsidian text-white overflow-hidden p-4 font-sans"
      data-testid="login-page"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 z-0 bg-obsidian">
        <CodeRain density={0.4} speed={0.5} fade={0.08} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(255,26,58,0.15) 0%, transparent 50%), radial-gradient(ellipse at 50% 100%, rgba(0,224,255,0.08) 0%, transparent 50%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/"
          className="block text-center mb-6 font-mono text-[10px] tracking-[0.4em] text-zinc-500 hover:text-rune-red transition-colors"
          data-testid="back-home"
        >
          ← VOLVER AL OJO
        </Link>

        <div
          className="glass-panel-blood clip-rune relative overflow-hidden"
          data-testid="login-card"
        >
          {/* Top neon line */}
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, #ff1a3a 30%, #00e0ff 70%, transparent)",
              boxShadow: "0 0 10px rgba(255,26,58,0.8)",
            }}
          />

          <div className="p-8">
            <div className="text-center mb-7">
              <div
                className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-sm border border-rune-red/50"
                style={{
                  background:
                    "radial-gradient(ellipse at 30% 30%, #ff1a3a 0%, #b8001f 40%, #220006 100%)",
                  boxShadow:
                    "0 0 24px rgba(255, 26, 58, 0.5), inset 0 0 12px rgba(0,0,0,0.5)",
                }}
              >
                <Eye size={20} className="text-white" strokeWidth={2.5} />
              </div>
              <h1
                className="font-runic text-3xl tracking-[0.18em] text-white"
                style={{
                  textShadow:
                    "0 0 18px rgba(255, 26, 58, 0.45), 0 0 4px rgba(255,255,255,0.4)",
                }}
                data-testid="login-title"
              >
                ACCESO
              </h1>
              <div className="text-[10px] font-mono tracking-[0.5em] text-data-cyan mt-2">
                ── IDENTIFÍCATE ──
              </div>
            </div>

            <form
              onSubmit={submit}
              className="space-y-3"
              data-testid="login-form"
              autoComplete="on"
            >
              <div className="relative">
                <AtSign
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-rune-red/70 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="email o teléfono"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  inputMode="email"
                  data-testid="login-identifier"
                  className="w-full bg-black/50 border border-rune-red/25 pl-10 pr-3 py-2.5 text-sm font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-rune-red focus:shadow-[0_0_18px_rgba(255,26,58,0.35)] rounded-sm transition-all"
                />
              </div>
              <div className="relative">
                <Lock
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-rune-red/70 pointer-events-none"
                />
                <input
                  type="password"
                  placeholder="contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="w-full bg-black/50 border border-rune-red/25 pl-10 pr-3 py-2.5 text-sm font-mono text-white placeholder:text-zinc-600 focus:outline-none focus:border-rune-red focus:shadow-[0_0_18px_rgba(255,26,58,0.35)] rounded-sm transition-all"
                />
              </div>

              <label
                className="flex items-center gap-2 text-[11px] font-mono tracking-widest text-zinc-400 cursor-pointer select-none pt-1"
                data-testid="remember-label"
              >
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="accent-rune-red w-3 h-3"
                  data-testid="remember-checkbox"
                  style={{ accentColor: "#ff1a3a" }}
                />
                RECORDARME EN ESTE DISPOSITIVO
              </label>

              {error && (
                <div
                  className="text-xs font-mono text-rune-red border border-rune-red/40 bg-rune-red/10 px-3 py-2 rounded-sm"
                  data-testid="login-error"
                  style={{ boxShadow: "0 0 14px rgba(255,26,58,0.15)" }}
                >
                  ⚠ {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit"
                className="w-full relative px-7 py-3 clip-rune font-runic tracking-[0.3em] text-sm text-white transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background:
                    "linear-gradient(135deg, #ff1a3a 0%, #b8001f 50%, #5a0008 100%)",
                  boxShadow:
                    "0 0 24px rgba(255,26,58,0.4), inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -8px 18px rgba(90,0,8,0.5)",
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> AUTENTICANDO…
                    </>
                  ) : (
                    <>
                      <Eye size={14} strokeWidth={2.5} /> ENTRAR
                    </>
                  )}
                </span>
              </button>

              <div className="text-center text-[10px] font-mono tracking-widest text-zinc-500 mt-6 pt-4 border-t border-rune-red/15">
                ¿NO TIENES CUENTA?{" "}
                <Link
                  to="/registro"
                  className="text-rune-red hover:text-white hover:underline transition-colors"
                  data-testid="link-register"
                >
                  REGISTRARSE
                </Link>
              </div>
            </form>
          </div>

          {/* Bottom neon line */}
          <div
            className="absolute bottom-0 left-0 right-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, #00e0ff 30%, #ff1a3a 70%, transparent)",
              boxShadow: "0 0 10px rgba(0,224,255,0.6)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
